import { usePostHog } from "posthog-js/react";
import { createContext, useCallback, useContext } from "react";
import {
	readAnalyticsConsent,
	useAnalyticsConsentState,
} from "@/lib/analytics-consent";
import { contactTarget, currentPageProperties } from "@/lib/analytics-payload";
import { stripLocaleFromPathname } from "@/lib/i18n/locale";

export const AnalyticsReadyContext = createContext(false);

export const ANALYTICS_EVENTS = {
	pageView: "$pageview",
	pageLeave: "$pageleave",
	ctaClick: "cta_click",
	contentView: "content_view",
	contentRead: "content_read",
	contentClick: "content_click",
	sectionView: "section_view",
	localeSwitch: "locale_switch",
} as const;

export type AnalyticsEventName =
	(typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type PageType =
	| "home"
	| "blog_index"
	| "blog_post"
	| "docs_index"
	| "doc"
	| "product"
	| "other";

export type ContentType = "blog" | "doc" | "product";

const utmKeys = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
] as const;

export type UtmKey = (typeof utmKeys)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

export const getUtmParams = (search: string): UtmParams => {
	if (!search) return {};
	const query = search.startsWith("?") ? search : `?${search}`;
	const params = new URLSearchParams(query);
	const result: UtmParams = {};

	for (const key of utmKeys) {
		const value = params.get(key);
		if (value) {
			result[key] = value;
		}
	}

	return result;
};

export const normalizePathname = (pathname: string) => {
	if (!pathname || pathname === "/") return "/";
	return pathname.replace(/\/+$/, "");
};

export const getPageContext = (pathname: string) => {
	const normalized = normalizePathname(pathname);

	if (normalized === "/") {
		return { pageType: "home" } as const;
	}
	if (normalized === "/blog") {
		return { pageType: "blog_index", contentType: "blog" } as const;
	}
	if (normalized.startsWith("/blog/")) {
		return {
			pageType: "blog_post",
			contentType: "blog",
			slug: normalized.replace("/blog/", ""),
		} as const;
	}
	if (normalized === "/docs") {
		return { pageType: "docs_index", contentType: "doc" } as const;
	}
	if (normalized.startsWith("/docs/")) {
		return {
			pageType: "doc",
			contentType: "doc",
			slug: normalized.replace("/docs/", ""),
		} as const;
	}
	if (normalized.startsWith("/products/")) {
		return {
			pageType: "product",
			contentType: "product",
			slug: normalized.replace("/products/", ""),
		} as const;
	}

	return { pageType: "other" } as const;
};

export const buildPageViewProperties = ({
	pathname,
	searchStr,
	hash,
	locale,
}: {
	pathname: string;
	searchStr: string;
	hash: string;
	locale: string;
}) => {
	const basePathname = stripLocaleFromPathname(pathname);
	const { pageType, contentType, slug } = getPageContext(basePathname);
	const utmParams = getUtmParams(searchStr);

	return omitUndefined({
		path: normalizePathname(basePathname),
		page_type: pageType,
		content_type: contentType,
		slug,
		locale,
		hash: hash ? hash.replace(/^#/, "") : undefined,
		...utmParams,
	});
};

const omitUndefined = <T extends Record<string, unknown>>(value: T) =>
	Object.fromEntries(
		Object.entries(value).filter(([, entry]) => entry !== undefined),
	) as {
		[K in keyof T]: Exclude<T[K], undefined>;
	};

const ignoreProperties = (_properties: Record<string, unknown>) => {};
const ignoreException = (
	_error: unknown,
	_properties?: Record<string, unknown>,
) => {};

export const useAnalytics = () => {
	const client = usePostHog();
	const ready = useContext(AnalyticsReadyContext);
	const state = useAnalyticsConsentState();
	const capture = useCallback(
		(event: AnalyticsEventName, properties?: Record<string, unknown>) => {
			if (
				!ready ||
				readAnalyticsConsent() !== "granted" ||
				!client?.has_opted_in_capturing()
			)
				return;
			const common = {
				...currentPageProperties(
					event === ANALYTICS_EVENTS.pageView
						? properties?.page_url
						: undefined,
				),
				locale: properties?.locale ?? document.documentElement.lang,
			};
			if (event === ANALYTICS_EVENTS.pageView) {
				client.capture(event, common, { send_instantly: true });
			} else if (event === ANALYTICS_EVENTS.ctaClick) {
				const target = contactTarget(properties?.href);
				if (!target) return;
				client.capture(
					event,
					{ ...common, target, placement: properties?.placement },
					{ send_instantly: true },
				);
			}
		},
		[client, ready],
	);
	return {
		capture,
		register: ignoreProperties,
		registerOnce: ignoreProperties,
		captureException: ignoreException,
		canCapture:
			ready && state === "granted" && Boolean(client?.has_opted_in_capturing()),
	};
};

export const useSectionViewTracking = (
	_sections: { id: string; label: string }[],
	_options: { pageType?: PageType; locale?: string } = {},
) => {};

export const useContentReadTracking = (_options: {
	contentType: "blog" | "doc";
	slug: string;
	title?: string;
	locale?: string;
}) => {};
